import React from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title, DoughnutController } from "chart.js";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";
import { RiskCategoryExposure } from "api/api-risk-dashboard.d";

ChartJS.register(ArcElement, Tooltip, Legend, Title, DoughnutController);

// Using the interface already defined in api-risk-dashboard.d.ts

interface RiskCategoryPieChartProps {
  riskCategoryData?: RiskCategoryExposure;
  isLoading?: boolean;
  error?: any;
  showTitle?: boolean;
}

const RiskCategoryPieChart: React.FC<RiskCategoryPieChartProps> = ({ 
  riskCategoryData: data,
  isLoading = false,
  error = null,
  showTitle = false
}) => {
  const theme = useTheme();
  
  // Define vibrant colors for risk levels
  const riskLevelColors = {
    High: "#F44336", // Bright Red
    Medium: "#FFC107", // Amber
    Low: "#4CAF50", // Green
    "Medium-High": "#FF9800", // Orange
    "Medium-Low": "#8BC34A", // Light Green
  };

  // Default vibrant colors for chart segments if risk level doesn't match
  const defaultColors = [
    "#3F51B5", // Indigo
    "#2196F3", // Blue
    "#009688", // Teal
    "#9C27B0", // Purple
    "#E91E63", // Pink
  ];
  
  // Transform API data for pie chart
  const createChartData = () => {
    // Early return if loading or data not ready
    if (isLoading) {
      return null;
    }
    
    // Safely check if data exists and has the expected structure
    if (!data?.data || !data.data["risk-category"]) {
      return null;
    }
    
    const riskCategories = data.data["risk-category"];
    
    // Handle empty array
    if (!Array.isArray(riskCategories) || riskCategories.length === 0) {
      return null;
    }
    
    return {
      labels: riskCategories.map((item) => `${item.risk_level} (${item.count})`),
      datasets: [
        {
          data: riskCategories.map((item) => item.count),
          backgroundColor: riskCategories.map((item, index) => 
            riskLevelColors[item.risk_level as keyof typeof riskLevelColors] || defaultColors[index % defaultColors.length]
          ),
          borderColor: "#FFFFFF",
          borderWidth: 2,
          hoverOffset: 15,
          hoverBackgroundColor: riskCategories.map((item, index) => {
            const color = riskLevelColors[item.risk_level as keyof typeof riskLevelColors] || defaultColors[index % defaultColors.length];
            return `${color}CC`; // Add transparency for hover effect using template literal
          }),
        },
      ],
    };
  };

  const chartData = createChartData();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Typography color="error">Failed to load risk category data</Typography>
      </Box>
    );
  }

  if (!chartData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px", flexDirection: "column", gap: 2 }}>
        <Typography color="text.secondary">No risk category data available</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
          Data may still be loading or the API response format is unexpected
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      {showTitle && (
        <Typography variant="h6" align="center" sx={{ mb: 2 }}>Risk Category Exposure</Typography>
      )}
      <Box sx={{ height: showTitle ? "calc(100% - 50px)" : "100%" }}>
        <Pie 
          data={chartData} 
          options={{ 
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  usePointStyle: true,
                  padding: 15,
                  color: theme.palette.text.primary,
                  font: {
                    weight: 'bold'
                  },
                  boxWidth: 10
                }
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                bodyFont: {
                  size: 14
                },
                callbacks: {
                  label: function (context) {
                    const label = context.label || '';
                    const value = context.raw || 0;
                    const dataset = context.dataset;
                    const total = dataset.data.reduce((acc: number, data: number) => acc + data, 0);
                    const percentage = Math.round((value as number / total) * 100);
                    return `${label}: ${percentage}% (Count: ${value})`;
                  },
                  title: function (tooltipItems) {
                    return tooltipItems[0].label.split(' (')[0]; // Only show risk level in title
                  }
                }
              }
            },
            animation: {
              animateScale: true,
              animateRotate: true
            }
          }} 
        />
      </Box>
    </Box>
  );
};

export default RiskCategoryPieChart;
